import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const SiteContentForm = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [heroImageFile, setHeroImageFile] = useState(null);
    const [cloudinaryConfig, setCloudinaryConfig] = useState(null);
    const defaultSectionBackgrounds = {
        about: { type: 'gradient', color1: '#e53e3e', color2: '#2d3748', angle: 90 },
        vehicles: { type: 'solid', color1: '#111827', color2: '#f3f4f6', angle: 90 },
        members: { type: 'solid', color1: '#1F2937', color2: '#f3f4f6', angle: 90 }
    };

    const [formData, setFormData] = useState({
        title: '',
        logo: { url: '', alt: '' },
        heroImage: { url: '', alt: '' },
        colors: { siteBackground: '#ffffff', navbarBackground: '#1f2937' },
        sectionBackgrounds: { ...defaultSectionBackgrounds }
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch Cloudinary config
                const configRef = doc(db, 'config', 'cloudinary');
                const configSnap = await getDoc(configRef);
                if (configSnap.exists()) {
                    setCloudinaryConfig(configSnap.data());
                } else {
                    throw new Error('Cloudinary konfigurácia nebola nájdená. Prosím, nastavte ju v sekcii Konfigurace.');
                }

                // Fetch site content
                const contentRef = doc(db, 'content', 'site');
                const contentSnap = await getDoc(contentRef);
                if (contentSnap.exists()) {
                    const data = contentSnap.data();
                    setFormData(prev => ({
                        ...prev,
                        ...data,
                        colors: { ...prev.colors, ...(data.colors || {}) },
                        sectionBackgrounds: { ...defaultSectionBackgrounds, ...(data.sectionBackgrounds || {}) },
                        logo: { ...(data.logo || prev.logo) },
                        heroImage: { ...(data.heroImage || prev.heroImage) },
                        title: data.title || prev.title || ''
                    }));
                } else {
                }
            } catch (err) {
                console.error('Error fetching initial data:', err);
                setError(err.message || 'Chyba při načítání dát.');
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setLogoFile(e.target.files[0]);
        }
    };

    const handleHeroFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setHeroImageFile(e.target.files[0]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'logo.alt') {
            setFormData(prev => ({ ...prev, logo: { ...prev.logo, alt: value } }));
        } else if (name === 'heroImage.alt') {
            setFormData(prev => ({ ...prev, heroImage: { ...prev.heroImage, alt: value } }));
        } else if (name === 'colors.siteBackground' || name === 'colors.navbarBackground') {
            const key = name.split('.')[1];
            setFormData(prev => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
        } else if (name === 'title') {
            setFormData(prev => ({ ...prev, title: value }));
        }
    };

    const handleSectionChange = (section, field, value) => {
        setFormData(prev => {
            const prevSections = prev.sectionBackgrounds || {};
            const prevSection = prevSections[section] || defaultSectionBackgrounds[section] || { type: 'solid', color1: '#ffffff', color2: '#f3f4f6', angle: 90 };
            return {
                ...prev,
                sectionBackgrounds: {
                    ...prevSections,
                    [section]: {
                        ...prevSection,
                        [field]: value
                    }
                }
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!cloudinaryConfig?.cloud_name || !cloudinaryConfig?.upload_preset) {
            setError("Cloudinary konfigurácia nie je kompletná.");
            setLoading(false);
            return;
        }

        try {
            let logoUrl = formData.logo?.url || '';
            let heroImageUrl = formData.heroImage?.url || '';
            const uploadPreset = cloudinaryConfig.upload_preset;

            if (logoFile) {
                const fd = new FormData();
                fd.append('file', logoFile);
                fd.append('upload_preset', uploadPreset);

                const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloud_name}/image/upload`, {
                    method: 'POST',
                    body: fd,
                });

                const data = await response.json();
                if (!response.ok) {
                    console.error('Cloudinary upload error:', data);
                    throw new Error(data.error?.message || 'Chyba nahrání loga');
                }
                logoUrl = data.secure_url;
            }

            if (heroImageFile) {
                const fd = new FormData();
                fd.append('file', heroImageFile);
                fd.append('upload_preset', uploadPreset);

                const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloud_name}/image/upload`, {
                    method: 'POST',
                    body: fd,
                });

                const data = await response.json();
                if (!response.ok) {
                    console.error('Cloudinary upload error:', data);
                    throw new Error(data.error?.message || 'Chyba nahrání obrázku pozadí');
                }
                heroImageUrl = data.secure_url;
            }


            const payload = { colors: { ...formData.colors } };
            const hasLogoUrl = !!logoUrl;
            const hasAlt = !!(formData.logo?.alt && formData.logo.alt.trim());
            if (hasLogoUrl || hasAlt) {
                payload.logo = { url: logoUrl, alt: formData.logo?.alt || '' };
            }

            const hasHeroImageUrl = !!heroImageUrl;
            const hasHeroAlt = !!(formData.heroImage?.alt && formData.heroImage.alt.trim());
            if (hasHeroImageUrl || hasHeroAlt) {
                payload.heroImage = { url: heroImageUrl, alt: formData.heroImage?.alt || '' };
            }

            if (formData.sectionBackgrounds) {
                payload.sectionBackgrounds = { ...formData.sectionBackgrounds };
            }

            if (formData.title && formData.title.trim()) {
                payload.title = formData.title.trim();
            }

            const docRef = doc(db, 'content', 'site');
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                await updateDoc(docRef, payload);
            } else {
                const initial = { colors: { ...formData.colors } };
                if (hasLogoUrl || hasAlt) initial.logo = { url: logoUrl, alt: formData.logo?.alt || '' };
                if (hasHeroImageUrl || hasHeroAlt) initial.heroImage = { url: heroImageUrl, alt: formData.heroImage?.alt || '' };
                initial.sectionBackgrounds = { ...(formData.sectionBackgrounds || defaultSectionBackgrounds) };
                if (formData.title && formData.title.trim()) initial.title = formData.title.trim();
                await setDoc(docRef, initial);
            }

            setFormData(prev => ({ ...prev, ...payload, sectionBackgrounds: { ...defaultSectionBackgrounds, ...(payload.sectionBackgrounds || prev.sectionBackgrounds) } }));
            setLogoFile(null);
            setHeroImageFile(null);
            setSuccess(true);
        } catch (err) {
            console.error('Error saving site content:', err);
            setError(err.message || 'Nepodařilo se uložit data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-6">Načítám konfiguraci...</div>;

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Kontent stránky</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <h3 className="text-lg font-semibold mb-2">Název stránky</h3>
                    <label className="block text-sm font-medium mb-1">Title (zobrazen v navigaci)</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title || ''}
                        onChange={handleChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3"
                        placeholder="Např. Sbor dobrovolných hasičů"
                    />
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-2">Logo</h3>
                    {formData.logo?.url && (
                        <div className="mb-2">
                            <img src={formData.logo.url} alt={formData.logo.alt || 'logo'} className="h-16" />
                        </div>
                    )}
                    <div className="mb-2">
                        <label className="block text-sm font-medium mb-1">Nahrát nové logo</label>
                        <input className="border border-gray-200 p-3 rounded-md mb-4" type="file" accept="image/*" onChange={handleFileChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Alt text loga</label>
                        <input type="text" name="logo.alt" value={formData.logo?.alt || ''} onChange={handleChange} className="shadow appearance-none border rounded w-full py-2 px-3" />
                    </div>

                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-2">Hero page pozadí</h3>
                    {formData.heroImage?.url && (
                        <div className="mb-2">
                            <img src={formData.heroImage.url} alt={formData.heroImage.alt || 'hero background'} className="h-32" />
                        </div>
                    )}
                    <div className="mb-2">
                        <label className="block text-sm font-medium mb-1">Nahrát nové pozadí</label>
                        <input className="border border-gray-200 p-3 rounded-md mb-4" type="file" accept="image/*" onChange={handleHeroFileChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Alt text pozadí</label>
                        <input type="text" name="heroImage.alt" value={formData.heroImage?.alt || ''} onChange={handleChange} className="shadow appearance-none border rounded w-full py-2 px-3" />
                    </div>
                </div>


                <div>
                    <h3 className="text-lg font-semibold mb-2">Barvy</h3>
                    <div className="flex items-center space-x-4">
                        <label className="text-sm">Pozadí navigace</label>
                        <input type="color" name="colors.navbarBackground" value={formData.colors?.navbarBackground || '#1f2937'} onChange={handleChange} />
                        <span className="text-sm">{formData.colors?.navbarBackground}</span>
                    </div>
                    <div className="flex items-center space-x-4 mt-2">
                        <label className="text-sm">Pozadí stránky</label>
                        <input type="color" name="colors.siteBackground" value={formData.colors?.siteBackground || '#ffffff'} onChange={handleChange} />
                        <span className="text-sm">{formData.colors?.siteBackground}</span>
                    </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-2">Pozadí sekcí</h3>
                    {['about', 'vehicles', 'members'].map((sectionKey) => {
                        const cfg = formData.sectionBackgrounds?.[sectionKey] || defaultSectionBackgrounds[sectionKey] || { type: 'solid', color1: '#ffffff', color2: '#f3f4f6', angle: 90 };
                        const label = sectionKey === 'about' ? 'VÍCE O NÁS' : sectionKey === 'vehicles' ? 'VOZIDLA' : 'ČLENOVÉ';
                        const previewStyle = cfg.type === 'solid'
                            ? { background: cfg.color1 }
                            : { background: `linear-gradient(${cfg.angle}deg, ${cfg.color1}, ${cfg.color2})` };
                        return (
                            <div key={sectionKey} className="border border-gray-200 p-4 rounded-md mb-4">
                                <h4 className="text-md font-medium mb-2">{label}</h4>

                                <div className="mb-2">
                                    <span className="mr-4 text-sm">Typ</span>
                                    <label className="mr-3">
                                        <input
                                            type="radio"
                                            name={`${sectionKey}-type`}
                                            value="solid"
                                            checked={cfg.type === 'solid'}
                                            onChange={() => handleSectionChange(sectionKey, 'type', 'solid')}
                                        />
                                        <span className="ml-1">Plná barva</span>
                                    </label>
                                    <label className="mr-3">
                                        <input
                                            type="radio"
                                            name={`${sectionKey}-type`}
                                            value="gradient"
                                            checked={cfg.type === 'gradient'}
                                            onChange={() => handleSectionChange(sectionKey, 'type', 'gradient')}
                                        />
                                        <span className="ml-1">Gradient</span>
                                    </label>
                                </div>

                                <div className="mb-2 flex items-center flex-wrap gap-6">
                                    <div>
                                        <label className="block text-sm">Barva 1</label>
                                        <input
                                            type="color"
                                            value={cfg.color1}
                                            onChange={(e) => handleSectionChange(sectionKey, 'color1', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm">Barva 2</label>
                                        <input
                                            type="color"
                                            value={cfg.color2}
                                            onChange={(e) => handleSectionChange(sectionKey, 'color2', e.target.value)}
                                            disabled={cfg.type === 'solid'}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm">Úhel (°)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="360"
                                            value={cfg.angle}
                                            onChange={(e) => handleSectionChange(sectionKey, 'angle', Number(e.target.value || 0))}
                                            className="w-24"
                                            disabled={cfg.type === 'solid'}
                                        />
                                    </div>
                                </div>

                                <div className="text-sm text-gray-600">Náhled:</div>
                                <div className="mt-2 h-12 w-full rounded border" style={previewStyle} />
                            </div>
                        );
                    })}
                </div>

                <div className="pt-4 border-t">
                    <button type="submit" className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700">Uložit změny</button>
                    {success && <span className="ml-3 text-sm text-green-600">Změny byly uloženy.</span>}
                    {error && <span className="ml-3 text-sm text-red-600">{error}</span>}
                </div>
            </form>
        </div>
    );
};

export default SiteContentForm;
